import * as React from 'react';
import { debounce } from 'debounce';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import IconButton from '@mui/material/IconButton';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import { Translate, translateDateFormat } from '../../../../../../../shared/LanguageService';
import { PHOTOGRAPHER_BOOKING_DATE_FORMAT } from '../../bookingDateSelect/state/dateHelper';
import { AvailableTimeslot, Forecast } from '../../types';
import { TimeslotSelectionDialog } from '../../availableTimeslotsSection/view/TimeslotSelectionDialog';
import { tryGetForecastIcon } from '../../availableTimeslotsSection/state/forecastHelper';

type BookingFooterProps = {
    availableTimeslots: AvailableTimeslot[];
    forecast?: Forecast;
    isOpen: boolean;
    selectedDate: string | null;
    selectedPhotographer: string | null;
    selectedTimeslot: AvailableTimeslot | null;
    onChangeTimeslot: (timeslot: AvailableTimeslot) => void;
    onCancel?: () => void;
    onBook?: (timeslot: string) => void;
};

export enum NavState {
    CLOSED = 'inactive',
    OPEN = 'active'
}

function BookingSection({
    date,
    forecast,
    photographer,
    onCancel,
    onBook
}: {
    date: string;
    forecast?: Forecast;
    photographer: string;
    onCancel?: () => void;
    onBook?: (timeslot: string) => void;
}) {
    let [selectedTime, selectedDate] = date.split(';');
    let [isBooking, setIsBooking] = React.useState(false);

    let forecastIcon = forecast ? tryGetForecastIcon(forecast.skyIcon) : null;

    return (
        <>
            <Box
                sx={{
                    margin: 0,
                    display: 'flex',
                    gap: '8px',
                    height: '100%',
                    justifyContent: 'space-between'
                }}
            >
                <Typography
                    component="h2"
                    variant="body1"
                    sx={{
                        fontSize: '1.6rem',
                        fontFamily: 'Gilroy',
                        fontWeight: '700'
                    }}
                >
                    <time dateTime={selectedTime}>{selectedTime}</time>,
                </Typography>
                <Box
                    sx={{
                        display: 'flex',
                        gap: '8px',
                        alignItems: 'center'
                    }}
                >
                    <Typography
                        component="h2"
                        variant="body1"
                        sx={{
                            fontSize: '16px',
                            fontFamily: 'Gilroy',
                            fontWeight: '700',
                            textTransform: 'capitalize'
                        }}
                    >
                        <time dateTime={selectedDate}>{selectedDate}</time>
                    </Typography>
                    {forecast && forecastIcon ? (
                        <img alt="weather forecast" src={forecastIcon} width="32" height="32" />
                    ) : null}
                </Box>
            </Box>
            <Box
                component="dl"
                sx={{
                    margin: 0,
                    display: 'flex',
                    gap: '8px'
                }}
            >
                <Typography
                    component="dt"
                    variant="body1"
                    sx={{
                        fontFamily: 'Gilroy',
                        fontWeight: '700'
                    }}
                >
                    {Translate('se360/photographerbooking/page/text/photographer')}:
                </Typography>
                <Typography component="dd" variant="body1">
                    {photographer}
                </Typography>
            </Box>
            <Box
                sx={{
                    display: 'flex',
                    gap: '8px',
                    justifyContent: 'flex-end'
                }}
            >
                <Button
                    variant="outlined"
                    onClick={() => {
                        if (typeof onCancel === 'function') {
                            onCancel();
                        }
                    }}
                >
                    {Translate('cancel')}
                </Button>
                <Button
                    variant="contained"
                    disabled={isBooking}
                    onClick={() => {
                        setIsBooking(true);
                        if (typeof onBook === 'function') {
                            onBook(date);
                        }
                    }}
                >
                    {`${Translate('se360/photographerbooking/page/button/book-photographer')}${
                        isBooking ? '...' : ''
                    }`}
                </Button>
            </Box>
        </>
    );
}

function SelectTimeSection({
    availableTimeslots,
    forecast,
    selectedDate,
    onCancel,
    onSelect
}: {
    availableTimeslots: AvailableTimeslot[];
    forecast?: Forecast;
    selectedDate: string;
    onCancel?: () => void;
    onSelect: (timeslot: AvailableTimeslot) => void;
}) {
    let [availableTimeslotIndex, setAvailableTimeIndex] = React.useState(0);
    let [showTimeslotSelectionDialog, setShowTimeslotSelectionDialog] = React.useState(false);
    let timeslot = availableTimeslots[availableTimeslotIndex];

    let forecastIcon = forecast ? tryGetForecastIcon(forecast.skyIcon) : null;

    function handleTimeslotChange(timeslot: AvailableTimeslot) {
        let index = availableTimeslots.findIndex(
            (t) => t.time === timeslot.time && t.date === timeslot.date
        );

        if (index > -1) {
            setAvailableTimeIndex(index);
        }

        setShowTimeslotSelectionDialog(false);
    }

    function handleCloseTimeslotSelectionDialog() {
        setShowTimeslotSelectionDialog(false);
    }

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '16px',
                height: '100%',
                justifyContent: 'space-between'
            }}
        >
            <Box sx={{ display: 'flex' }}>
                <Typography
                    variant="body1"
                    sx={{
                        fontSize: '16px',
                        fontFamily: 'Gilroy',
                        color: 'text.secondary'
                    }}
                >
                    {Translate('se360/photographerbooking/customer/calendar/booking-footer/hint')}
                </Typography>
                <Box sx={{ flex: '1 auto' }} />
                <Typography
                    variant="body1"
                    sx={{
                        fontSize: '10px',
                        fontFamily: 'Gilroy',
                        color: 'text.secondary'
                    }}
                >
                    {availableTimeslotIndex + 1}/{availableTimeslots.length}
                </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Typography
                    variant="body1"
                    sx={{
                        textTransform: 'capitalize',
                        fontSize: '16px',
                        fontWeight: '700'
                    }}
                >
                    <time dateTime={selectedDate}>{selectedDate}</time>
                </Typography>
                {forecast && forecastIcon ? (
                    <img alt="weather forecast" src={forecastIcon} width="32" height="32" />
                ) : null}
            </Box>
            <Box
                sx={{
                    display: 'flex',
                    gap: '4px',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}
            >
                <IconButton
                    disabled={availableTimeslotIndex === 0}
                    onClick={debounce(() => {
                        if (availableTimeslotIndex > 0) {
                            setAvailableTimeIndex((prev) => prev - 1);
                        }
                    }, 100)}
                >
                    <ArrowBackIosNewIcon />
                </IconButton>
                <Button
                    variant="text"
                    onClick={() => {
                        setShowTimeslotSelectionDialog(true);
                    }}
                    sx={{
                        fontSize: '32px',
                        fontFamily: 'Gilroy',
                        fontWeight: '700',
                        userSelect: 'none',
                        textTransform: 'capitalize',
                        textDecoration: 'none'
                    }}
                >
                    <time dateTime={timeslot.time}>{timeslot.time}</time>
                </Button>
                <IconButton
                    disabled={availableTimeslotIndex === availableTimeslots.length - 1}
                    onClick={debounce(() => {
                        if (availableTimeslotIndex < availableTimeslots.length - 1) {
                            setAvailableTimeIndex((prev) => prev + 1);
                        }
                    }, 100)}
                >
                    <ArrowForwardIosIcon />
                </IconButton>
            </Box>
            <Box
                sx={{
                    display: 'flex',
                    gap: '4px',
                    justifyContent: 'center'
                }}
            >
                <Button
                    variant="outlined"
                    onClick={() => {
                        if (typeof onCancel === 'function') {
                            onCancel();
                        }
                    }}
                >
                    {Translate('cancel')}
                </Button>
                <Button
                    variant="contained"
                    onClick={() => {
                        onSelect(timeslot);
                    }}
                >
                    {Translate('se360/photographerbooking/customer/calendar/button/select-time')}
                </Button>
            </Box>
            <TimeslotSelectionDialog
                availableTimeslots={availableTimeslots}
                isOpen={showTimeslotSelectionDialog}
                onChange={handleTimeslotChange}
                onClose={handleCloseTimeslotSelectionDialog}
                labels={{
                    timeslotSelectionDialogTitle: Translate(
                        'se360/photographerbooking/customer/calendar/timeslot-selection-dialog/title'
                    ),
                    closeTimeslotSelection: Translate('cancel')
                }}
            />
        </Box>
    );
}

let BookingFooter = React.forwardRef<HTMLElement, BookingFooterProps>(function Footer(
    {
        availableTimeslots,
        forecast,
        isOpen,
        selectedDate, // This is the initially selected timeslot
        selectedPhotographer,
        selectedTimeslot, // This is the adjusted time on the selected date
        onChangeTimeslot,
        onCancel,
        onBook
    }: BookingFooterProps,
    ref
) {
    if (!selectedDate) {
        return null;
    }

    if (availableTimeslots.length === 1) {
        return (
            <Box
                ref={ref}
                component="nav"
                data-state={isOpen ? NavState.OPEN : NavState.CLOSED}
                className="booking-footer__container"
                sx={{
                    position: 'fixed',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    zIndex: 100,
                    backgroundColor: 'rgba(255, 255, 255, 1)',
                    padding: '20px 16px',
                    border: '1px solid rgba(224, 224, 224, 1)',
                    boxShadow: '0px 0px 0px 1px rgba(224, 224, 224, 1)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px'
                }}
            >
                {selectedDate && selectedPhotographer ? (
                    <BookingSection
                        date={selectedDate}
                        forecast={forecast}
                        photographer={selectedPhotographer}
                        onCancel={onCancel}
                        onBook={onBook}
                    />
                ) : null}
            </Box>
        );
    }

    if (selectedTimeslot) {
        return (
            <Box
                ref={ref}
                component="nav"
                data-state={isOpen ? NavState.OPEN : NavState.CLOSED}
                className="booking-footer__container"
                sx={{
                    position: 'fixed',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    zIndex: 100,
                    backgroundColor: 'rgba(255, 255, 255, 1)',
                    padding: '20px 16px',
                    border: '1px solid rgba(224, 224, 224, 1)',
                    boxShadow: '0px 0px 0px 1px rgba(224, 224, 224, 1)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px'
                }}
            >
                <BookingSection
                    date={`${selectedTimeslot.time};${translateDateFormat(
                        new Date(selectedTimeslot.date),
                        PHOTOGRAPHER_BOOKING_DATE_FORMAT
                    )}`}
                    forecast={forecast}
                    photographer={selectedPhotographer}
                    onCancel={onCancel}
                    onBook={onBook}
                />
            </Box>
        );
    }

    let [_, dateSelected] = selectedDate.split(';');

    return (
        <Box
            ref={ref}
            component="nav"
            data-state={isOpen ? NavState.OPEN : NavState.CLOSED}
            className="booking-footer__container"
            sx={{
                position: 'fixed',
                bottom: 0,
                left: 0,
                right: 0,
                zIndex: 100,
                backgroundColor: 'rgba(255, 255, 255, 1)',
                padding: '20px 16px',
                border: '1px solid rgba(224, 224, 224, 1)',
                boxShadow: '0px 0px 0px 1px rgba(224, 224, 224, 1)',
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                height: '50vh'
            }}
        >
            <SelectTimeSection
                availableTimeslots={availableTimeslots}
                forecast={forecast}
                selectedDate={dateSelected}
                onCancel={onCancel}
                onSelect={onChangeTimeslot}
            />
        </Box>
    );
});

export { BookingFooter };
